import { useState } from "react";
import { History } from "lucide-react";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/app/admin/_common/ui/table";
import { Badge } from "@/app/admin/_common/ui/badge";
import CuoEmpty from "@/app/admin/_common/CuoEmpty";
import { formatUtcDate } from "@/app/_utils/dayjs";
import { cn } from "@/app/_utils/cn";
import type { AutomationRun } from "@/app/_services/AutomationService";

import {
  RUN_STATUS_BADGE_CLASSES,
  RUN_STATUS_DOT_CLASSES,
  RUN_STATUS_LABEL,
  toRunStatus,
} from "../../../_cmp/automationStatus";
import RunDetailDrawer from "./RunDetailDrawer";

export function RunStatusPill({ status }: { status: string }) {
  const key = toRunStatus(status);
  return (
    <Badge
      variant="outline"
      className={cn("gap-1.5 rounded-[7px] font-normal", RUN_STATUS_BADGE_CLASSES[key])}
    >
      <span className={cn("size-1.5 flex-none rounded-full", RUN_STATUS_DOT_CLASSES[key])} />
      {RUN_STATUS_LABEL[key]}
    </Badge>
  );
}

export default function RunsTable({ runs }: { runs: AutomationRun[] }) {
  const [selected, setSelected] = useState<AutomationRun | null>(null);

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-md border border-border bg-card">
      <Table
        containerClassName="min-h-0 flex-1 overflow-x-auto overflow-y-auto"
        className={cn("min-w-[820px] table-fixed", runs.length === 0 && "h-full")}
      >
        <TableHeader>
          <TableRow className="sticky top-0 z-10 hover:bg-muted [&>th]:bg-muted">
            <TableHead className="h-8 w-[16%] text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              When
            </TableHead>
            <TableHead className="h-8 w-[12%] text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Status
            </TableHead>
            <TableHead className="h-8 w-[22%] text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Recipient
            </TableHead>
            <TableHead className="h-8 w-[26%] text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Subject
            </TableHead>
            <TableHead className="h-8 w-[24%] text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Detail
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {runs.length === 0 ? (
            <TableRow className="h-full hover:bg-transparent">
              <TableCell colSpan={5} className="h-full p-0">
                <CuoEmpty icon={History} text="No runs yet" />
              </TableCell>
            </TableRow>
          ) : (
            runs.map((run) => (
              <TableRow
                key={run.id}
                className="group cursor-pointer"
                onClick={() => setSelected(run)}
              >
                <TableCell className="whitespace-nowrap py-1.5 text-[12.5px] text-muted-foreground">
                  {formatUtcDate(run.created_at)}
                </TableCell>
                <TableCell className="py-1.5">
                  <RunStatusPill status={run.status} />
                </TableCell>
                <TableCell className="truncate py-1.5 text-[12.5px] text-muted-foreground">
                  {run.recipient || "-"}
                </TableCell>
                <TableCell className="truncate py-1.5 text-[13px] text-foreground">
                  <span className="group-hover:underline">
                    {run.rendered_subject || "-"}
                  </span>
                </TableCell>
                <TableCell className="truncate py-1.5 text-[12.5px] text-muted-foreground">
                  {/* skip_reason and error are the two things worth surfacing without
                      opening the row: "skipped" alone never explains itself. */}
                  {run.error || run.skip_reason || "-"}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      <RunDetailDrawer run={selected} onClose={() => setSelected(null)} />
    </div>
  );
}
